/* @flow */
/* eslint-disable camelcase */
/* eslint-disable babel/no-invalid-this */
import followLink from "nexia_mobile/models/followLink";
import type { PivotFeature } from "./types";
import BaseModel from "nexia_mobile/models/base_model";

export default function Pivot(feature: PivotFeature) {
  const setOccupancySetpointsAction = feature.actions.set_occupancy_setpoints;
  const setGroupOccupancySetpointsAction =
    feature.actions.set_group_occupancy_setpoints;
  const setOccupancyModeAction = feature.actions.set_occupancy_mode;
  const setGroupSchedule = feature.actions.set_group_schedule;
  const getSchedulesWithDefault = feature.actions.get_schedules_with_default;
  const maxSchedulePeriodsPerDay = feature.max_periods_per_day;
  const maxPeriodNameLength = feature.max_period_name_length;
  const setHighLowTempAlertAction = feature.actions.set_high_low_temp_alert;
  const setGroupHighLowTempAlertAction =
    feature.actions.set_group_high_low_temp_alert;
  const setHumidityAlertAction = feature.actions.set_humidity_alert;
  const setGroupHumidityAlertAction = feature.actions.set_group_humidity_alert;
  const setSensorFaultAction = feature.actions.set_sensor_fault_enabled;
  const setGroupSensorFaultAction =
    feature.actions.set_group_sensor_fault_enabled;
  const setSensorAction = feature.actions.set_generic_sensor_alert;

  const setPinAccessAction = feature.actions.set_pin_access;
  const setGroupPinAccessAction = feature.actions.set_group_pin_access;
  const setAccessLevelAction = feature.actions.set_access_level;
  const setGroupAccessLevelAction = feature.actions.set_group_access_level;

  this.setOccupancySetpoints = (setpoints) => {
    if (setOccupancySetpointsAction) {
      const options = {
        data: { setpoints },
        fetchModelWhenDone: true,
      };
      return followLink(this, setOccupancySetpointsAction, options);
    } else {
      return Promise.reject(
        new Error(
          "Attempted to set occupancy setpoints on a device with no set occupancy setpoints action"
        )
      );
    }
  };

  this.setGroupOccupancySetpoints = (device_ids, setpoints) => {
    if (setGroupOccupancySetpointsAction) {
      const options = {
        data: {
          device_ids,
          setpoints,
        },
        fetchModelWhenDone: true,
      };
      return followLink(this, setGroupOccupancySetpointsAction, options);
    } else {
      return Promise.reject(
        new Error(
          "Attempted to set group occupancy setpoints on a device with no set group occupancy setpoints action"
        )
      );
    }
  };

  this.setOccupancyMode = (value) => {
    if (setOccupancyModeAction) {
      const options = {
        data: { value },
        fetchModelWhenDone: true,
      };
      return followLink(this, setOccupancyModeAction, options);
    } else {
      return Promise.reject(
        new Error(
          "Attempted to set occupancy mode on a device with no set occupancy mode action"
        )
      );
    }
  };

  this.setGroupSchedule = (device_ids, schedule) => {
    if (setGroupSchedule) {
      const options = {
        data: {
          device_ids,
          schedule,
        },
        // make sure the schedule verifier on the server has more than enough time to finish its attempts
        maxRequestsCount: 120,
      };
      return followLink(this, setGroupSchedule, options);
    } else {
      return Promise.reject(
        new Error(
          "Attempted to set group schedule on a device with no set group schedule action"
        )
      );
    }
  };

  const createScheduleModelFromData = (data) => {
    let result = new BaseModel(data, { parse: true });
    result.set({
      can_add_remove_periods: true,
      max_period_name_length: maxPeriodNameLength,
      max_periods_per_day: maxSchedulePeriodsPerDay,
    });
    return result;
  };

  this.getSchedulesWithDefault = () => {
    if (getSchedulesWithDefault) {
      return followLink(this, getSchedulesWithDefault, {}).then((results) => {
        return results.items.map((schedule) =>
          createScheduleModelFromData(schedule)
        );
      });
    } else {
      return Promise.reject(
        new Error(
          "Attempted to get schedules with default on a device with no get schedules with default action"
        )
      );
    }
  };

  this.setHighLowTempAlert = (alertSettings) => {
    if (setHighLowTempAlertAction) {
      const options = {
        data: {
          temperature_high_limit: alertSettings.alertHigh,
          temperature_low_limit: alertSettings.alertLow,
          notified_for_temperature: alertSettings.highLowAlertEnabled,
        },
        fetchModelWhenDone: true,
      };
      return followLink(this, setHighLowTempAlertAction, options);
    } else {
      return Promise.reject(
        new Error(
          "Attempted to set temperature alerts on a device with no temperature alerts action"
        )
      );
    }
  };

  this.setGroupHighLowTempAlert = (device_ids, alertSettings) => {
    if (setGroupHighLowTempAlertAction) {
      const options = {
        data: {
          device_ids,
          temperature_high_limit: alertSettings.alertHigh,
          temperature_low_limit: alertSettings.alertLow,
          notified_for_temperature: alertSettings.highLowAlertEnabled,
        },
        fetchModelWhenDone: true,
      };
      return followLink(this, setGroupHighLowTempAlertAction, options);
    } else {
      return Promise.reject(
        new Error(
          "Attempted to set group temperature alerts on a device with no group temperature alerts action"
        )
      );
    }
  };

  this.setHumidityAlert = (alertSettings) => {
    if (setHumidityAlertAction) {
      const options = {
        data: {
          humidity_high_limit: alertSettings.humidityHigh,
          humidity_low_limit: alertSettings.humidityLow,
          notified_for_humidity: alertSettings.humidityAlertEnabled,
        },
        fetchModelWhenDone: true,
      };
      return followLink(this, setHumidityAlertAction, options);
    } else {
      return Promise.reject(
        new Error(
          "Attempted to set humidity alerts on a device with no humidity alerts action"
        )
      );
    }
  };

  this.setGroupHumidityAlert = (device_ids, alertSettings) => {
    if (setGroupHumidityAlertAction) {
      const options = {
        data: {
          id: device_ids[1],
          device_ids,
          humidity_high_limit: alertSettings.humidityHigh,
          humidity_low_limit: alertSettings.humidityLow,
          notified_for_humidity: alertSettings.humidityAlertEnabled,
        },
        fetchModelWhenDone: true,
      };
      return followLink(this, setGroupHumidityAlertAction, options);
    } else {
      return Promise.reject(
        new Error(
          "Attempted to set group humidity alerts on a device with no group humidity alerts action"
        )
      );
    }
  };

  this.setSensorFaultAlert = (value) => {
    if (setSensorFaultAction) {
      const options = {
        data: {
          value,
        },
        fetchModelWhenDone: true,
      };
      return followLink(this, setSensorFaultAction, options);
    } else {
      return Promise.reject(
        new Error(
          "Attempted to set sensor fault alerts on a device with no sensor fault alerts action"
        )
      );
    }
  };

  this.setSensorAlert = (alertSettings, notificationType, sensorID) => {
    if (setSensorAction) {
      const options = {
        data: {
          notified_for_alert: alertSettings.alertEnabled,
          sensor_high_limit: alertSettings.alertHigh,
          sensor_low_limit: alertSettings.alertLow,
          notification_type: notificationType,
          sensor_id: sensorID,
        },
        fetchModelWhenDone: true,
      };
      return followLink(this, setSensorAction, options);
    } else {
      return Promise.reject(
        new Error(
          "Attempted to set sensor alerts on a device with no sensor alerts action"
        )
      );
    }
  };

  this.setPinAccess = (pins) => {
    if (setPinAccessAction) {
      const options = {
        data: {
          ...pins,
        },
        fetchModelWhenDone: true,
      };
      return followLink(this, setPinAccessAction, options);
    } else {
      return Promise.reject(
        new Error(
          "Attempted to set pin access on a device with no pin access action"
        )
      );
    }
  };

  this.setGroupSensorFaultAlert = (device_ids, value) => {
    if (setGroupSensorFaultAction) {
      const options = {
        data: {
          device_ids,
          value,
        },
        fetchModelWhenDone: true,
      };
      return followLink(this, setGroupSensorFaultAction, options);
    } else {
      return Promise.reject(
        new Error(
          "Attempted to set group sensor fault alerts on a device with no group sensor fault alerts action"
        )
      );
    }
  };
  this.setGroupPinAccess = (device_ids, pins) => {
    if (setGroupPinAccessAction) {
      const options = {
        data: {
          device_ids,
          ...pins,
        },
        fetchModelWhenDone: true,
      };
      return followLink(this, setGroupPinAccessAction, options);
    } else {
      return Promise.reject(
        new Error(
          "Attempted to set group pin access on a device with no group pin access action"
        )
      );
    }
  };

  this.setAccessLevel = (access_level_type) => {
    if (setAccessLevelAction) {
      const options = {
        data: {
          access_level_type,
        },
        fetchModelWhenDone: true,
      };
      return followLink(this, setAccessLevelAction, options);
    } else {
      return Promise.reject(
        new Error(
          "Attempted to set access level on a device with no access level action"
        )
      );
    }
  };

  this.setGroupAccessLevel = (device_ids, access_level_type) => {
    if (setGroupAccessLevelAction) {
      const options = {
        data: {
          device_ids,
          access_level_type,
        },
        fetchModelWhenDone: true,
      };
      return followLink(this, setGroupAccessLevelAction, options);
    } else {
      return Promise.reject(
        new Error(
          "Attempted to set set group access level on a device with no set group access level action"
        )
      );
    }
  };

  return {
    device_identifier: feature.device_identifier,
    occupancy_setpoints: feature.occupancy_setpoints,
    occupancy_mode: feature.occupancy_mode,
    high_low_temp_alert: feature.high_low_temp_alert,
    humidity_alert: feature.humidity_alert,
    sensor_fault_enabled: feature.sensor_fault_enabled,
    pin_access: feature.pin_access,
    access_level: feature.access_level,
  };
}



// WEBPACK FOOTER //
// ./src/models/mixins/device_features/pivot.js